import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useConnectionVersion } from '@/hooks/useConnectionVersion'
import type { Database } from '@/lib/supabase'
import {
  applyChatContactScopeFilter,
  contactMatchesScope,
} from '@/lib/chatContactScope'
import { applyMessageActivity, dedupeAccountContacts } from '@/utils/contactUtils'

type AccountContact = Database['public']['Tables']['account_contacts']['Row']

const CONTACTS_PER_PAGE = 20
const PICTURE_REFRESH_INTERVAL = 24 * 60 * 60 * 1000
const PICTURE_FETCH_BATCH_SIZE = 5
const PICTURE_FETCH_DELAY = 2000

export interface ChatFilters {
  attributionId: string | null
  statusChat: string | null
  tag: string | null
}

interface UseContactsPaginatedReturn {
  contacts: AccountContact[]
  loading: boolean
  error: string | null
  hasMore: boolean
  loadMore: () => void
  refetch: () => Promise<void>
  getContactById: (contactId: string) => Promise<AccountContact | undefined>
  updateContactLocally: (contactId: string, updates: Partial<AccountContact>) => void
}

export const useContactsPaginated = (filters?: ChatFilters): UseContactsPaginatedReturn => {
  const { user } = useAuth()
  const { getUrl, version } = useConnectionVersion()
  const [contacts, setContacts] = useState<AccountContact[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const isLoadingMore = useRef(false)
  const fetchContactsRef = useRef<((pageNum: number, append: boolean) => Promise<void>) | null>(null)
  const contactsRef = useRef<AccountContact[]>([])
  const isFetchingPictures = useRef(false)
  const fetchedPictureIds = useRef<Set<string>>(new Set())

  // Sincronizar ref com state
  useEffect(() => {
    contactsRef.current = contacts
  }, [contacts])

  const fetchContactPicture = useCallback(async (contact: AccountContact): Promise<void> => {
    if (!user?.account_id || !supabase) return
    if (!contact.jid) return

    try {
      let contactNumber: string

      if (contact.jid.includes('@lid')) {
        contactNumber = contact.jid
      } else {
        const cleanJid = contact.jid.replace('@s.whatsapp.net', '').replace('@c.us', '').replace('@g.us', '')
        if (!cleanJid || !/^\d+$/.test(cleanJid)) {
          console.log('[Picture] Skipping non-numeric JID:', contact.jid)
          return
        }
        contactNumber = cleanJid
      }

      const apiUrl = getUrl('fetch-contact-picture')

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          contact_number: contactNumber,
          account_id: user.account_id,
        }),
      })

      if (!response.ok) {
        console.log('[Picture] Edge function returned error:', response.status)
        return
      }

      const data = await response.json() as { profilePictureUrl?: string | null }

      if (data.profilePictureUrl) {
        const { error: updateError } = await supabase
          .from('account_contacts')
          .update({
            profile_picture: data.profilePictureUrl,
            profile_picture_updated_at: new Date().toISOString(),
          })
          .eq('id', contact.id)
          .eq('account_id', user.account_id)

        if (!updateError) {
          console.log('[Picture] Updated picture for:', contact.jid)
          fetchedPictureIds.current.add(contact.id)
        }
      }
    } catch (err) {
      console.log('[Picture] Error fetching picture:', err)
    }
  }, [user?.account_id, version])

  const fetchContactsPictures = useCallback(async (contactsList: AccountContact[]): Promise<void> => {
    if (isFetchingPictures.current) return
    if (!user?.account_id) return

    isFetchingPictures.current = true

    try {
      const now = new Date().getTime()
      const contactsToFetch = contactsList.filter(contact => {
        if (fetchedPictureIds.current.has(contact.id)) return false
        if (contact.is_group) return false

        if (contact.profile_picture_updated_at) {
          const updatedAt = new Date(contact.profile_picture_updated_at).getTime()
          return now - updatedAt > PICTURE_REFRESH_INTERVAL
        }

        return true
      })

      if (contactsToFetch.length === 0) {
        console.log('[Picture] No contacts need picture update')
        isFetchingPictures.current = false
        return
      }

      console.log('[Picture] Fetching pictures for', contactsToFetch.length, 'contacts')

      for (let i = 0; i < contactsToFetch.length; i += PICTURE_FETCH_BATCH_SIZE) {
        const batch = contactsToFetch.slice(i, i + PICTURE_FETCH_BATCH_SIZE)

        await Promise.all(batch.map(contact => fetchContactPicture(contact)))

        if (i + PICTURE_FETCH_BATCH_SIZE < contactsToFetch.length) {
          await new Promise(resolve => setTimeout(resolve, PICTURE_FETCH_DELAY))
        }
      }
    } catch (err) {
      console.error('[Picture] Error fetching pictures batch:', err)
    } finally {
      isFetchingPictures.current = false
    }
  }, [user?.account_id, fetchContactPicture])

  const fetchContacts = useCallback(async (pageNum: number = 0, append: boolean = false): Promise<void> => {
    if (!user?.account_id || !supabase || isLoadingMore.current) {
      if (!append) {
        setLoading(false)
      }
      return
    }

    try {
      if (!append) {
        setLoading(true)
      }
      isLoadingMore.current = true
      setError(null)

      const from = pageNum * CONTACTS_PER_PAGE
      const to = from + CONTACTS_PER_PAGE - 1

      // Sem `count: 'exact'`: ele obrigava o Postgres a varrer todas as linhas que casam
      // com o filtro só para decidir se existe próxima página — e a página cheia já
      // responde isso. Mesmo critério que a paginação de mensagens usa.
      let query = supabase
        .from('account_contacts')
        .select('*')
        .eq('account_id', user.account_id)

      query = applyChatContactScopeFilter(query, user)

      if (filters?.attributionId) {
        if (filters.attributionId === '__unassigned__') {
          query = query.is('attribution_id', null)
        } else {
          query = query.eq('attribution_id', filters.attributionId)
        }
      }

      if (filters?.statusChat) {
        query = query.eq('status_chat', filters.statusChat)
      }

      if (filters?.tag) {
        query = query.contains('tags', [filters.tag.toLowerCase()])
      }

      const { data: contactsData, error: contactsError } = await query
        .order('last_message_at', { ascending: false, nullsFirst: false })
        .range(from, to)

      if (contactsError) throw contactsError

      const newContacts = contactsData || []

      if (append) {
        setContacts(prev => {
          const existingIds = new Set(prev.map(c => c.id))
          const uniqueNew = newContacts.filter(c => !existingIds.has(c.id))
          const merged = dedupeAccountContacts([...prev, ...uniqueNew])
          fetchContactsPictures(merged)
          return merged
        })
      } else {
        const deduped = dedupeAccountContacts(newContacts)
        setContacts(deduped)
        fetchContactsPictures(deduped)
      }

      // Página cheia = provavelmente há mais. No pior caso (o total é múltiplo exato do
      // tamanho da página) gasta-se uma requisição vazia no fim — mesma troca da
      // paginação de mensagens, e bem mais barata que um COUNT exato por página.
      setHasMore(newContacts.length === CONTACTS_PER_PAGE)
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar contatos')
    } finally {
      setLoading(false)
      isLoadingMore.current = false
    }
  }, [
    user?.account_id,
    user?.view_all_contacts,
    user?.view_unassigned_contacts,
    user?.auth_user_id,
    user?.type,
    fetchContactsPictures,
    filters?.attributionId,
    filters?.statusChat,
    filters?.tag,
  ])

  // Atualizar a referência sempre que fetchContacts mudar
  useEffect(() => {
    fetchContactsRef.current = fetchContacts
  }, [fetchContacts])

  // Resetar paginação quando filtros mudarem
  const scopeKey = `${user?.view_all_contacts ?? ''}-${user?.view_unassigned_contacts ?? ''}-${user?.auth_user_id || ''}-${user?.type || ''}`
  const filtersKey = `${filters?.attributionId || ''}-${filters?.statusChat || ''}-${filters?.tag || ''}-${scopeKey}`
  const prevFiltersKeyRef = useRef(filtersKey)

  useEffect(() => {
    if (prevFiltersKeyRef.current !== filtersKey) {
      prevFiltersKeyRef.current = filtersKey
      setPage(0)
      setHasMore(true)
      setContacts([])
      if (fetchContactsRef.current) {
        fetchContactsRef.current(0, false)
      }
    }
  }, [filtersKey])

  const loadMore = useCallback(() => {
    if (!loading && hasMore && !isLoadingMore.current && fetchContactsRef.current) {
      const nextPage = page + 1
      setPage(nextPage)
      fetchContactsRef.current(nextPage, true)
    }
  }, [loading, hasMore, page])

  const refetch = useCallback(async (): Promise<void> => {
    setPage(0)
    setHasMore(true)
    await fetchContacts(0, false)
  }, [fetchContacts])

  const getContactById = useCallback(async (contactId: string): Promise<AccountContact | undefined> => {
    // Usar ref para acessar contacts sem criar dependência
    const currentContacts = contactsRef.current;

    // Primeiro tenta encontrar na lista local
    const localContact = currentContacts.find(contact => contact.id === contactId)
    if (localContact) {
      return localContact
    }

    // Se não encontrar localmente, busca no banco
    if (!user?.account_id || !supabase) {
      return undefined
    }

    try {
      console.log('🔍 [getContactById] Fetching contact from database:', contactId)
      let scoped = applyChatContactScopeFilter(
        supabase
          .from('account_contacts')
          .select('*')
          .eq('account_id', user.account_id)
          .eq('id', contactId),
        user
      )
      const { data, error } = await scoped.maybeSingle()

      if (error) {
        console.error('❌ [getContactById] Error fetching contact:', error)
        return undefined
      }

      if (data) {
        console.log('✅ [getContactById] Contact found in database')
        // NÃO adicionar aos contacts aqui - deixar o realtime fazer isso
        // Isso previne o loop infinito
      }

      return data || undefined
    } catch (err) {
      console.error('❌ [getContactById] Exception:', err)
      return undefined
    }
  }, [
    user?.account_id,
    user?.auth_user_id,
    user?.view_all_contacts,
    user?.view_unassigned_contacts,
    user?.type,
  ])

  // Avalia se um contato satisfaz os filtros ativos
  const matchesFilters = useCallback((contact: AccountContact): boolean => {
    if (filters?.attributionId) {
      if (filters.attributionId === '__unassigned__') {
        if (contact.attribution_id !== null && contact.attribution_id !== undefined) return false
      } else if (contact.attribution_id !== filters.attributionId) {
        return false
      }
    }

    if (filters?.statusChat) {
      const contactStatus = (contact as any).status_chat ?? 'aberto'
      if (contactStatus !== filters.statusChat) return false
    }

    if (filters?.tag) {
      const tags = (contact.tags as string[] | null) || []
      if (!tags.includes(filters.tag)) return false
    }

    if (!contactMatchesScope(user, contact)) return false

    return true
  }, [filters?.attributionId, filters?.statusChat, filters?.tag, user])

  const updateContactLocally = useCallback((contactId: string, updates: Partial<AccountContact>) => {
    console.log('⚡ [updateContactLocally] Updating contact optimistically:', contactId)
    setContacts(prev => {
      const current = prev.find(c => c.id === contactId)
      if (!current) return prev

      const updated = { ...current, ...updates } as AccountContact

      // Se o contato deixou de corresponder aos filtros após o update, remover
      if (!matchesFilters(updated)) {
        console.log('🚫 [updateContactLocally] Contact no longer matches filters, removing:', contactId)
        return prev.filter(c => c.id !== contactId)
      }

      // Reordena por last_message_at (mesmo critério do fetch/realtime) para
      // conversas subirem ao topo quando last_message_at é atualizado.
      const others = prev.filter(c => c.id !== contactId)
      return dedupeAccountContacts([updated, ...others])
    })
  }, [matchesFilters])

  useEffect(() => {
    if (!user?.account_id || !supabase) {
      return
    }

    console.log('🔄 [useContactsPaginated] Setting up contacts subscription')

    fetchContacts(0, false)

    const channel = supabase
      .channel('contacts-changes-paginated')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'account_contacts',
          filter: `account_id=eq.${user.account_id}`
        },
        (payload) => {
          console.log('📇 [useContactsPaginated] Contact change detected:', payload.eventType)

          if (payload.eventType === 'INSERT') {
            const newContact = payload.new as AccountContact
            if (!matchesFilters(newContact)) {
              console.log('⏭️ [useContactsPaginated] New contact does not match filters, skipping')
              return
            }
            setContacts(prev => {
              const exists = prev.some(c => c.id === newContact.id)
              if (exists) return prev
              console.log('✅ [useContactsPaginated] Adding new contact to top')
              return dedupeAccountContacts([newContact, ...prev])
            })
          } else if (payload.eventType === 'UPDATE') {
            const updatedContact = payload.new as AccountContact

            // Se o contato deixou de corresponder aos filtros, remover da lista
            if (!matchesFilters(updatedContact)) {
              setContacts(prev => {
                if (!prev.some(c => c.id === updatedContact.id)) return prev
                console.log('🚫 [useContactsPaginated] Contact no longer matches filters, removing:', updatedContact.id)
                return prev.filter(c => c.id !== updatedContact.id)
              })
              return
            }

            setContacts(prev => {
              console.log('🔄 [useContactsPaginated] Updating contact:', updatedContact.id)
              const filtered = prev.filter(c => c.id !== updatedContact.id)
              return dedupeAccountContacts([updatedContact, ...filtered])
            })
          } else if (payload.eventType === 'DELETE') {
            const deletedId = (payload.old as AccountContact).id
            setContacts(prev => {
              console.log('🗑️ [useContactsPaginated] Removing contact:', deletedId)
              return prev.filter(c => c.id !== deletedId)
            })
          }
        }
      )
      // Rede de segurança para a bolinha de não lido.
      //
      // A ordem no banco é: INSERT em account_messages → trigger sobe
      // account_contacts.last_message_at → evento UPDATE de account_contacts. Se esse
      // segundo evento se perde (reconexão, aba suspensa, ou a própria RLS derrubando a
      // entrega sob carga), a lista fica com o last_message_at antigo — igual ao
      // last_read_at — e a conversa parece lida mesmo com mensagem nova.
      //
      // O INSERT da mensagem já chega neste navegador de qualquer forma, então aplicamos
      // aqui o mesmo efeito do trigger. É idempotente: se o UPDATE também chegar, grava o
      // mesmo valor. Sem conexão nova — é um listener a mais no canal que já existe.
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'account_messages',
          filter: `account_id=eq.${user.account_id}`
        },
        (payload) => {
          const message = payload.new as {
            account_contact_id?: string | null
            sent_at?: string | null
          }
          if (!message?.account_contact_id || !message.sent_at) return

          const contactId = message.account_contact_id
          const sentAt = message.sent_at

          setContacts(prev => applyMessageActivity(prev, contactId, sentAt))
        }
      )
      .subscribe((status) => {
        console.log('🔌 [useContactsPaginated] Subscription status:', status)
      })

    return () => {
      console.log('🔌 [useContactsPaginated] Unsubscribing from contacts')
      if (supabase) {
        supabase.removeChannel(channel)
      }
    }
  }, [user?.account_id, fetchContacts, matchesFilters])

  return {
    contacts,
    loading,
    error,
    hasMore,
    loadMore,
    refetch,
    getContactById,
    updateContactLocally,
  }
}
